/**
 * Single source of truth for per-integration memory modalities (text, audio, image, video)
 * used by integration cards, MemoryTypesBadges, FeatureSupportMatrix, and landing pages.
 *
 * Update this file when product support changes; do not duplicate levels in FeatureSupportMatrix.
 */

export type ModalityId = 'text' | 'audio' | 'image' | 'video';

/** Badge / capability mapping (compatible with MemoryTypesBadges). */
export interface IntegrationMemoryCapabilities {
  canContribute: string[];
  canAccess: string[];
  limitations?: Record<string, string>;
  /** Per-modality level for styling (optional; derived from profile) */
  levels?: Partial<Record<ModalityId, ModalityLevel>>;
}

/** User-facing support level (badges and matrix) */
export type ModalityLevel = 'full' | 'limited' | 'planned' | 'blocked' | 'none';

export interface ModalityProfile {
  text: ModalityLevel;
  audio: ModalityLevel;
  image: ModalityLevel;
  video: ModalityLevel;
  /** Optional per-modality notes (tooltips, matrix Notes column when applicable) */
  notes?: Partial<Record<ModalityId, string>>;
}

/** Matrix row support column (matches previous FeatureSupportMatrix) */
export type MatrixSupportLevel =
  | 'full'
  | 'limited'
  | 'planned'
  | 'not_supported'
  | 'blocked';

export type MatrixImplementationStatus = 'live' | 'not_implemented' | 'blocked';

export interface AudioMatrixRow {
  rowKey: string;
  support: MatrixSupportLevel;
  status: MatrixImplementationStatus;
  notes: string;
}

const N = (s: string): string => s;

/**
 * Curated audio support matrix (order preserved for UX).
 * Row keys: registry ids or `extra_*` for non-directory channels.
 */
export const AUDIO_MATRIX_ORDER = [
  'bare_api',
  'whatsapp_basic',
  'extra_python_sdk',
  'bare_mcp',
  'n8n',
  'zapier',
  'custom_gpt_action',
  'chrome_extension',
  'extra_web_app',
] as const;

export type AudioMatrixRowKey = (typeof AUDIO_MATRIX_ORDER)[number];

const AUDIO_MATRIX_DATA: Record<AudioMatrixRowKey, Omit<AudioMatrixRow, 'rowKey'>> = {
  bare_api: {
    support: 'full',
    status: 'live',
    notes: N('Native multipart upload, Whisper transcription'),
  },
  whatsapp_basic: {
    support: 'full',
    status: 'live',
    notes: N(
      'Voice notes need a paid plan (Starter or above). Voice messages via Twilio, automatic transcription'
    ),
  },
  extra_python_sdk: {
    support: 'full',
    status: 'live',
    notes: N('create_audio_memory() method, v0.2.0 published'),
  },
  bare_mcp: {
    support: 'limited',
    status: 'not_implemented',
    notes: N('URL references only (no binary support in MCP protocol)'),
  },
  n8n: {
    support: 'planned',
    status: 'not_implemented',
    notes: N('Technical capability exists, node update needed (Week 2-3)'),
  },
  zapier: {
    support: 'limited',
    status: 'not_implemented',
    notes: N('URL references only (planned, Month 2+)'),
  },
  custom_gpt_action: {
    support: 'blocked',
    status: 'blocked',
    notes: N('Blocked by OpenAI platform limitations'),
  },
  chrome_extension: {
    support: 'planned',
    status: 'not_implemented',
    notes: N('Web Audio API support (planned, Month 3+)'),
  },
  extra_web_app: {
    support: 'full',
    status: 'live',
    notes: N(
      'Memory Workspace: file upload, in-browser recording (WebM), Whisper transcription'
    ),
  },
};

export function getAudioMatrixRows(): AudioMatrixRow[] {
  return AUDIO_MATRIX_ORDER.map((rowKey) => ({
    rowKey,
    ...AUDIO_MATRIX_DATA[rowKey],
  }));
}

/** Registry integration ids only (excludes extra matrix rows). */
export const REGISTRY_MEMORY_MODALITIES: Record<string, ModalityProfile> = {
  claude_desktop: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('URL references only (MCP protocol; same limitation as Bare MCP)'),
    },
  },
  custom_gpt_action: {
    text: 'full',
    audio: 'blocked',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Blocked by OpenAI platform limitations'),
    },
  },
  slack_bot: {
    text: 'full',
    audio: 'none',
    image: 'full',
    video: 'none',
  },
  discord_bot: {
    text: 'full',
    audio: 'full',
    image: 'full',
    video: 'none',
    notes: {
      audio: N('Voice channel / transcript capture as productized'),
    },
  },
  microsoft_teams: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Meeting transcripts and chat; subject to Teams app scope'),
    },
  },
  telegram_bot: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Voice messages when supported by bot flow'),
    },
  },
  whatsapp_basic: {
    text: 'full',
    audio: 'full',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Voice notes need a paid plan (Starter or above).'),
    },
  },
  whatsapp_business: {
    text: 'full',
    audio: 'full',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Voice notes need a paid plan (Starter or above).'),
    },
  },
  zapier: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('URL references only (planned, Month 2+)'),
    },
  },
  make_com: {
    text: 'full',
    audio: 'planned',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Automation actions; audio support following API parity'),
    },
  },
  n8n: {
    text: 'full',
    audio: 'planned',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('Technical capability exists, node update needed (Week 2-3)'),
    },
  },
  dify: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('REST tools; binary audio via workflows as supported by your stack'),
    },
  },
  ifttt: {
    text: 'full',
    audio: 'none',
    image: 'none',
    video: 'none',
  },
  notion: {
    text: 'full',
    audio: 'none',
    image: 'limited',
    video: 'none',
    notes: {
      image: N('Embeds and page content as text; rich media when synced'),
    },
  },
  google_workspace: {
    text: 'full',
    audio: 'limited',
    image: 'limited',
    video: 'none',
    notes: {
      audio: N('Drive/Meet content as productized'),
    },
  },
  github: {
    text: 'full',
    audio: 'none',
    image: 'none',
    video: 'none',
  },
  cursor: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('URL references only (MCP protocol)'),
    },
  },
  chrome_extension: {
    text: 'full',
    audio: 'planned',
    image: 'limited',
    video: 'none',
    notes: {
      audio: N('Web Audio API support (planned, Month 3+)'),
    },
  },
  iphone_share_extension: {
    text: 'full',
    audio: 'none',
    image: 'full',
    video: 'none',
  },
  twitter_bookmarks: {
    text: 'full',
    audio: 'none',
    image: 'limited',
    video: 'none',
  },
  bare_api: {
    text: 'full',
    audio: 'full',
    image: 'full',
    video: 'none',
  },
  bare_mcp: {
    text: 'full',
    audio: 'limited',
    image: 'none',
    video: 'none',
    notes: {
      audio: N('URL references only (no binary support in MCP protocol)'),
    },
  },
};

const DEFAULT_PROFILE: ModalityProfile = {
  text: 'full',
  audio: 'none',
  image: 'none',
  video: 'none',
};

export function getRegistryMemoryModalities(integrationId: string): ModalityProfile {
  return REGISTRY_MEMORY_MODALITIES[integrationId] ?? DEFAULT_PROFILE;
}

/** Discord's audio note stays in the profile and is not shown. */
function tooltipNote(
  integrationId: string,
  modality: ModalityId,
  note: string | undefined
): string | undefined {
  if (integrationId === 'discord_bot' && modality === 'audio') {
    return undefined;
  }
  return note;
}

function levelToCapability(
  modality: ModalityId,
  level: ModalityLevel,
  note: string | undefined,
  caps: IntegrationMemoryCapabilities
): void {
  if (!caps.limitations) caps.limitations = {};
  if (!caps.levels) caps.levels = {};

  caps.levels[modality] = level;

  switch (level) {
    case 'full':
      caps.canContribute.push(modality);
      caps.canAccess.push(modality);
      if (note) caps.limitations[modality] = note;
      break;
    case 'limited':
      if (note) caps.limitations[modality] = note;
      else caps.limitations[modality] = 'Limited support';
      break;
    case 'planned':
      if (note) caps.limitations[modality] = note;
      else caps.limitations[modality] = 'Planned';
      break;
    case 'blocked':
      if (note) caps.limitations[modality] = note;
      else caps.limitations[modality] = 'Not supported';
      break;
    case 'none':
    default:
      break;
  }
}

/** Maps modality profile to badge capabilities (read by MemoryTypesBadges). */
export function getIntegrationMemoryCapabilities(
  integrationId: string
): IntegrationMemoryCapabilities {
  const profile = getRegistryMemoryModalities(integrationId);
  const caps: IntegrationMemoryCapabilities = {
    canContribute: [],
    canAccess: [],
    limitations: {},
    levels: {},
  };

  (['text', 'audio', 'image', 'video'] as const).forEach((m) => {
    const level = profile[m];
    const note = tooltipNote(integrationId, m, profile.notes?.[m]);
    levelToCapability(m, level, note, caps);
  });

  if (Object.keys(caps.limitations!).length === 0) {
    delete caps.limitations;
  }
  if (caps.levels && Object.keys(caps.levels).length === 0) {
    delete caps.levels;
  }

  return caps;
}

/** Audio filter: integrations where audio is usable (full or limited). */
export function integrationSupportsAudioContributeOrLimited(integrationId: string): boolean {
  const p = getRegistryMemoryModalities(integrationId);
  return p.audio === 'full' || p.audio === 'limited';
}
